import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { useToast } from '../ui/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Building2, CreditCard, FileText, CheckCircle, ArrowRight, 
  ArrowLeft, Sparkles, Shield, Loader2, X, Zap, Info
} from 'lucide-react';

const OnboardingWizard = ({ onComplete, onSkip }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  // Step 1: Company Info
  const [step1Data, setStep1Data] = useState({
    company_name: '',
    legal_form: 'AE',
    siren: '',
    siret: '',
    vat_number: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'France'
  });

  // Step 2: VAT & Preferences
  const [step2Data, setStep2Data] = useState({
    default_vat_rate: 20,
    default_payment_terms: 'Net 30',
    default_currency: 'EUR',
    bank_name: '',
    bank_iban: '',
    bank_bic: ''
  });

  // Step 3: First Invoice
  const [step3Data, setStep3Data] = useState({
    client_name: '',
    client_email: '',
    skip_invoice: false
  });

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const response = await profileAPI.getOnboardingStatus();
      if (response.data.is_completed) {
        if (onComplete) onComplete();
        return;
      }
      setCurrentStep(response.data.current_step);
    } catch (error) {
      console.error('Failed to check onboarding status');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleStep1Submit = async () => {
    // Validate SIREN
    const sirenClean = step1Data.siren.replace(/\s/g, '');
    if (sirenClean.length !== 9) {
      toast({
        title: 'SIREN invalide',
        description: 'Le SIREN doit contenir exactement 9 chiffres',
        variant: 'destructive'
      });
      return;
    }

    if (!step1Data.company_name || !step1Data.address) {
      toast({
        title: 'Champs requis',
        description: 'Veuillez remplir tous les champs obligatoires',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      await profileAPI.completeOnboardingStep1(step1Data);
      toast({
        title: 'Étape 1 terminée',
        description: 'Informations entreprise enregistrées'
      });
      setCurrentStep(2);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Échec de l\'enregistrement',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async () => {
    setLoading(true);
    try {
      await profileAPI.completeOnboardingStep2(step2Data);
      toast({
        title: 'Étape 2 terminée',
        description: 'Préférences enregistrées'
      });
      setCurrentStep(3);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Échec de l\'enregistrement',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStep3Submit = async () => {
    setLoading(true);
    try {
      const response = await profileAPI.completeOnboardingStep3(step3Data);
      toast({
        title: 'Bienvenue !',
        description: 'Votre compte est prêt pour la facturation 2026'
      });
      if (onComplete) onComplete();
      navigate(response.data.redirect_to || '/dashboard/smartfacture');
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Échec',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      await profileAPI.skipOnboarding();
      if (onSkip) onSkip();
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to skip onboarding');
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-emerald-50">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const steps = [
    { number: 1, title: 'Entreprise', icon: Building2 },
    { number: 2, title: 'TVA & Paiement', icon: CreditCard },
    { number: 3, title: 'Première facture', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-emerald-50/50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Zap className="h-8 w-8 text-emerald-600" />
            <span className="text-2xl font-bold text-slate-900">SmartFacture AI</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Configuration de votre compte
          </h1>
          <p className="text-slate-600">
            3 étapes pour être conforme à la facturation électronique 2026
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isActive = currentStep === step.number;
            const isCompleted = currentStep > step.number;
            
            return (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    isCompleted ? 'bg-emerald-500 text-white' :
                    isActive ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' :
                    'bg-slate-200 text-slate-500'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="h-6 w-6" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${
                    isActive ? 'text-emerald-600' : 'text-slate-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`w-16 h-1 mx-2 rounded ${
                    currentStep > step.number ? 'bg-emerald-500' : 'bg-slate-200'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        <Card className="border-slate-200 shadow-xl" data-testid="onboarding-wizard">
          {/* Step 1: Company Info */}
          {currentStep === 1 && (
            <>
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-emerald-50">
                <CardTitle className="flex items-center space-x-2">
                  <Building2 className="h-5 w-5 text-emerald-600" />
                  <span>Informations entreprise</span>
                </CardTitle>
                <CardDescription>
                  Ces informations sont obligatoires pour la facturation électronique 2026
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Compliance Alert */}
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Shield className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-emerald-800">Pourquoi ces informations ?</p>
                      <p className="text-sm text-emerald-700 mt-1">
                        À partir de septembre 2026, le SIREN et la raison sociale sont 
                        <strong> obligatoires</strong> sur toutes les factures électroniques envoyées via PPF/PDP.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-slate-700">Raison sociale *</Label>
                    <Input
                      value={step1Data.company_name}
                      onChange={(e) => setStep1Data(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Ma Société"
                      className="mt-1.5"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Forme juridique *</Label>
                    <Select
                      value={step1Data.legal_form}
                      onValueChange={(v) => setStep1Data(prev => ({ ...prev, legal_form: v }))}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EI">Entreprise Individuelle</SelectItem>
                        <SelectItem value="AE">Auto-entrepreneur</SelectItem>
                        <SelectItem value="EURL">EURL</SelectItem>
                        <SelectItem value="SARL">SARL</SelectItem>
                        <SelectItem value="SAS">SAS</SelectItem>
                        <SelectItem value="SASU">SASU</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700 flex items-center space-x-1">
                      <span>SIREN *</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">
                        Obligatoire 2026
                      </Badge>
                    </Label>
                    <Input
                      value={step1Data.siren}
                      onChange={(e) => setStep1Data(prev => ({ 
                        ...prev, 
                        siren: e.target.value.replace(/\D/g, '').slice(0, 9) 
                      }))}
                      placeholder="123456789"
                      className={`mt-1.5 ${
                        step1Data.siren && step1Data.siren.length === 9 
                          ? 'border-emerald-300' 
                          : step1Data.siren ? 'border-amber-300' : ''
                      }`}
                      data-testid="siren-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">9 chiffres - Trouvez-le sur societe.com</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700">SIRET (optionnel)</Label>
                    <Input
                      value={step1Data.siret}
                      onChange={(e) => setStep1Data(prev => ({ 
                        ...prev, 
                        siret: e.target.value.replace(/\D/g, '').slice(0, 14) 
                      }))}
                      placeholder="12345678900001"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">N° TVA Intracommunautaire</Label>
                    <Input
                      value={step1Data.vat_number}
                      onChange={(e) => setStep1Data(prev => ({ ...prev, vat_number: e.target.value.toUpperCase() }))}
                      placeholder="FR12345678901"
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-700">Adresse *</Label>
                  <Input
                    value={step1Data.address}
                    onChange={(e) => setStep1Data(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="123 Rue Exemple"
                    className="mt-1.5"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-700">Code postal *</Label>
                    <Input
                      value={step1Data.postal_code}
                      onChange={(e) => setStep1Data(prev => ({ ...prev, postal_code: e.target.value }))}
                      placeholder="75001"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Ville *</Label>
                    <Input
                      value={step1Data.city}
                      onChange={(e) => setStep1Data(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="Paris"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Pays</Label>
                    <Input
                      value={step1Data.country}
                      onChange={(e) => setStep1Data(prev => ({ ...prev, country: e.target.value }))}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t">
                  <Button variant="ghost" onClick={handleSkip} className="text-slate-500">
                    Passer pour l'instant
                  </Button>
                  <Button 
                    onClick={handleStep1Submit}
                    disabled={loading}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="step1-next-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowRight className="h-4 w-4 mr-2" />
                    )}
                    Continuer
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: VAT & Preferences */}
          {currentStep === 2 && (
            <>
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-teal-50">
                <CardTitle className="flex items-center space-x-2">
                  <CreditCard className="h-5 w-5 text-emerald-600" />
                  <span>TVA & Préférences de paiement</span>
                </CardTitle>
                <CardDescription>
                  Configurez vos paramètres par défaut pour gagner du temps
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700">Taux de TVA par défaut *</Label>
                    <Select
                      value={step2Data.default_vat_rate.toString()}
                      onValueChange={(v) => setStep2Data(prev => ({ ...prev, default_vat_rate: parseFloat(v) }))}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0% - Exonéré</SelectItem>
                        <SelectItem value="2.1">2.1% - Super réduit</SelectItem>
                        <SelectItem value="5.5">5.5% - Réduit</SelectItem>
                        <SelectItem value="10">10% - Intermédiaire</SelectItem>
                        <SelectItem value="20">20% - Normal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700">Conditions de paiement</Label>
                    <Select
                      value={step2Data.default_payment_terms}
                      onValueChange={(v) => setStep2Data(prev => ({ ...prev, default_payment_terms: v }))}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediate">Paiement immédiat</SelectItem>
                        <SelectItem value="Net 15">Net 15 jours</SelectItem>
                        <SelectItem value="Net 30">Net 30 jours</SelectItem>
                        <SelectItem value="Net 45">Net 45 jours</SelectItem>
                        <SelectItem value="Net 60">Net 60 jours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-700">Devise par défaut</Label>
                  <Select
                    value={step2Data.default_currency}
                    onValueChange={(v) => setStep2Data(prev => ({ ...prev, default_currency: v }))}
                  >
                    <SelectTrigger className="mt-1.5 w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                  <h4 className="font-medium text-slate-700 flex items-center space-x-2">
                    <CreditCard className="h-4 w-4" />
                    <span>Coordonnées bancaires (optionnel)</span>
                  </h4>
                  <p className="text-sm text-slate-500">
                    Ces informations apparaîtront sur vos factures pour faciliter le paiement
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Banque</Label>
                      <Input
                        value={step2Data.bank_name}
                        onChange={(e) => setStep2Data(prev => ({ ...prev, bank_name: e.target.value }))}
                        placeholder="BNP Paribas"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">IBAN</Label>
                      <Input
                        value={step2Data.bank_iban}
                        onChange={(e) => setStep2Data(prev => ({ ...prev, bank_iban: e.target.value.toUpperCase() }))}
                        placeholder="FR76..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">BIC</Label>
                      <Input
                        value={step2Data.bank_bic}
                        onChange={(e) => setStep2Data(prev => ({ ...prev, bank_bic: e.target.value.toUpperCase() }))}
                        placeholder="BNPAFRPP"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t">
                  <Button variant="ghost" onClick={() => setCurrentStep(1)}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Retour
                  </Button>
                  <Button 
                    onClick={handleStep2Submit}
                    disabled={loading}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="step2-next-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowRight className="h-4 w-4 mr-2" />
                    )}
                    Continuer
                  </Button>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: First Invoice */}
          {currentStep === 3 && (
            <>
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-purple-50">
                <CardTitle className="flex items-center space-x-2">
                  <Sparkles className="h-5 w-5 text-emerald-600" />
                  <span>Créez votre première facture</span>
                </CardTitle>
                <CardDescription>
                  Optionnel - vous pouvez aussi commencer plus tard
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Success message */}
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-emerald-800">Votre compte est prêt !</p>
                      <p className="text-sm text-emerald-700 mt-1">
                        Vous pouvez maintenant créer des factures conformes à la réforme 2026.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="text-slate-600">
                    Entrez le nom de votre premier client pour créer une facture immédiatement :
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">Nom du client</Label>
                      <Input
                        value={step3Data.client_name}
                        onChange={(e) => setStep3Data(prev => ({ ...prev, client_name: e.target.value }))}
                        placeholder="Acme Corp"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Email du client</Label>
                      <Input
                        type="email"
                        value={step3Data.client_email}
                        onChange={(e) => setStep3Data(prev => ({ ...prev, client_email: e.target.value }))}
                        placeholder="contact@acme.com"
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t">
                  <Button variant="ghost" onClick={() => setCurrentStep(2)}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Retour
                  </Button>
                  <div className="flex space-x-3">
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setStep3Data(prev => ({ ...prev, skip_invoice: true }));
                        handleStep3Submit();
                      }}
                      disabled={loading}
                    >
                      Passer
                    </Button>
                    <Button 
                      onClick={() => {
                        setStep3Data(prev => ({ ...prev, skip_invoice: false }));
                        handleStep3Submit();
                      }}
                      disabled={loading || !step3Data.client_name}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="step3-finish-btn"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileText className="h-4 w-4 mr-2" />
                      )}
                      Créer ma première facture
                    </Button>
                  </div>
                </div>
              </CardContent>
            </>
          )}
        </Card>

        {/* Help text */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Besoin d'aide ? Contactez-nous à support@smartbusiness.com
        </p>
      </div>
    </div>
  );
};

export default OnboardingWizard;
